import { createContext, useContext } from 'react';

import type { CustomTestCategory, CustomTestLanguage, CustomTestLifecycleState, CustomTestProduct, CustomTestWizardMode } from 'pages/custom_tests/types';
import type { QuestionTree } from 'pages/custom_tests/components/question_picker/QuestionPicker';
import type { EWizardStep } from 'pages/custom_tests/components/custom_test_wizard/machine';
import type { CustomTestProductQuestionCount } from 'apis/CustomTestApi';

export type CustomTestWizardContextValue = {
    name: string;

    /** Question-bank selector for hybrid categories: 1 = Live in the App, 2 = Browser Based. */
    bankModeId: number;
    categories: CustomTestCategory[];
    categoryId: number;
    editable: boolean;
    editStep: (step: EWizardStep) => void;
    fetchQuestions: () => Promise<void>;
    focusObjectiveId: number;
    handleBankModeChange: (bankModeId: number) => void;
    handleLanguageChange: (languageId: number) => void;
    handleProductsChange: (productIds: number[]) => void;
    handleQuestionsChange: (questionIds: string, totalSelected: number) => void;
    handleToggleCategory: (categoryId: number) => void;
    handleToggleProduct: (productId: number) => void;
    languageId: number;
    languages: CustomTestLanguage[];
    lifecycleState: CustomTestLifecycleState;
    loadingProducts: boolean;
    loadingQuestions: boolean;
    minScore: number;
    minutes: number;
    mode: CustomTestWizardMode;
    modeId: number;
    nameValidationError: string;
    orderMode: 'randomized' | 'grouped';
    productIds: number[];

    /** Per-program eligible-question counts for the create flows (empty on edit/copy). */
    productQuestionCounts: CustomTestProductQuestionCount[];
    products: CustomTestProduct[];

    /** Remount key for the Program dropdown; bumped when a question-invalidation dialog is cancelled. */
    programDropdownKey: number;
    questionIds: string;
    questionTree: QuestionTree;
    randomly: number;
    runSmartBuild: () => void;

    /** Engagement-viability verdict from the last generation (§12): '' until a draft is generated. */
    sbActivityState: '' | 'none' | 'low' | 'sufficient';
    sbClassroomId: number;
    sbClassrooms: SmartBuildClassroom[];
    sbClassroomsError: string;
    sbClassroomsLoading: boolean;
    sbCustomCount: number;

    /** Eligible question count for the current Smart Build scope selection. */
    sbEligibleCount: number;
    sbMyClassroomsOnly: boolean;

    /** Objectives available for Scope, derived from the loaded question tree. */
    sbObjectives: SmartBuildScopeOption[];
    sbPurpose: SmartBuildPurpose | '';
    sbScopeType: SmartBuildScopeType | '';
    sbScopeValueIds: number[];
    sbSizeChoice: SmartBuildSizeChoice | '';

    /** Topics (themes) available for Scope, derived from the loaded question tree. */
    sbTopics: SmartBuildScopeOption[];
    selectedQuestions: number;
    setProviderState: (state: Partial<Record<string, unknown>>) => void;
    setSbScopeType: (scopeType: SmartBuildScopeType | '') => void;
    setSbScopeValueIds: (ids: number[]) => void;
    setSbSizeChoice: (choice: SmartBuildSizeChoice | '') => void;
    settingsSeen: boolean;
    skipSmartBuild: () => void;
    smartBuildRun: boolean;
    totalQuestions: number;
    useRandomly: boolean;
    validationError: string;
};

// === Smart Build (statement-builder step) ===

/** Teacher-facing intents; each maps to a ranking strategy in the recommendation engine (§9.4). */
export type SmartBuildPurpose = 'weak-skills' | 'readiness' | 'mastery' | 'coverage-gaps' | 'recent-activity' | 'selected-content';

export type SmartBuildScopeType = 'all' | 'topic' | 'objective';

export type SmartBuildSizeChoice = 'quick' | 'standard' | 'custom';

export type SmartBuildClassroom = {
    Id: number;
    Instructor: string;
    Name: string;
    OwnerUserId: number;
};

export type SmartBuildScopeOption = {
    Id: number;
    Name: string;
    QuestionCount: number;
};

export const CustomTestWizardContext = createContext({} as CustomTestWizardContextValue);
CustomTestWizardContext.displayName = 'CustomTestWizardContext';

export const useCustomTestWizard = () => useContext(CustomTestWizardContext);
